<template>
  <v-card>
    <v-card-title>
      {{ formTitle }}
    </v-card-title>

    <v-card-text>
      <v-form
        ref="form"
        v-model="formValid"
      >
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="formData.name"
              label="名前"
              :rules="[v => !!v || '名前は必須です']"
              required
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              transition="scale-transition"
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="birthdayDisplay"
                  label="誕生日"
                  readonly
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearBirthday"
                />
              </template>
              <v-date-picker
                :value="birthdayPickerValue"
                locale="ja"
                :day-format="date => new Date(date).getDate()"
                no-title
                @input="onBirthdaySelect"
              />
            </v-menu>
            <v-checkbox
              v-model="includeYear"
              label="年を登録する"
              dense
              hide-details
              class="mt-0"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="formData.receiptAddressee"
              label="領収書宛名"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="blue darken-1"
        text
        :ripple="false"
        @click="onCancel"
      >
        キャンセル
      </v-btn>
      <v-btn
        color="primary"
        :loading="isSubmitting"
        :disabled="!formValid || isSubmitting"
        :ripple="false"
        @click="onSubmit"
      >
        {{ formSubmitText }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import {
  computed,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api'

export default {
  name: 'CustomerForm',
  props: {
    customer: {
      type: Object,
      default: () => null,
    },
    type: {
      type: String,
      default: 'new',
    },
  },
  setup(props, { emit }) {
    const form = ref(null)
    const formValid = ref(false)
    const isSubmitting = ref(false)
    const includeYear = ref(true)
    const formData = ref({
      name: '',
      birthday: null,
      receiptAddressee: '',
    })

    const isPersisted = computed(() => {
      return props.customer && props.customer.id
    })

    const isNew = computed(() => {
      return props.type === 'newUser' || !isPersisted.value
    })

    const formTitle = computed(() => {
      return isNew.value ? '顧客を追加' : '顧客を編集'
    })

    const formSubmitText = computed(() => {
      return isNew.value ? '作成' : '更新'
    })

    // 誕生日の表示用フォーマット
    const birthdayDisplay = computed(() => {
      if (!formData.value.birthday) return ''
      const date = new Date(formData.value.birthday)
      if (includeYear.value) {
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
      }

      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
    })

    // date-picker用の値（1900年を現在の年に変換して表示）
    const birthdayPickerValue = computed(() => {
      if (!formData.value.birthday) return null
      const date = new Date(formData.value.birthday)
      // 1900年の場合は今年に変換（picker表示用）
      if (date.getFullYear() === 1900) {
        const currentYear = new Date().getFullYear()

        return `${currentYear}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      }

      return formData.value.birthday
    })

    const onBirthdaySelect = dateString => {
      if (!dateString) {
        formData.value.birthday = null

        return
      }
      if (includeYear.value) {
        formData.value.birthday = dateString
      } else {
        // 年なしの場合は1900年で保存
        const parts = dateString.split('-')
        formData.value.birthday = `1900-${parts[1]}-${parts[2]}`
      }
    }

    const clearBirthday = () => {
      formData.value.birthday = null
    }

    const initialize = () => {
      if (props.customer) {
        const birthday = props.customer.attributes?.birthday || null
        formData.value = {
          name: props.customer.attributes?.name || '',
          birthday,
          receiptAddressee: props.customer.attributes?.receiptAddressee || '',
        }
        // 年が1900の場合は年なし設定
        if (birthday) {
          const year = new Date(birthday).getFullYear()
          includeYear.value = year !== 1900
        } else {
          includeYear.value = true
        }
      } else {
        formData.value = {
          name: '',
          birthday: null,
          receiptAddressee: '',
        }
        includeYear.value = true
      }
      isSubmitting.value = false
    }

    const onSubmit = async () => {
      if (!await form.value.validate() || isSubmitting.value) return

      isSubmitting.value = true
      const callback = () => {
        isSubmitting.value = false
      }

      emit('submit', {
        id: props.customer?.id,
        name: formData.value.name,
        birthday: formData.value.birthday,
        receiptAddressee: formData.value.receiptAddressee,
        callback,
      })
    }

    const onCancel = () => {
      initialize()
      emit('cancel')
    }

    watch(() => props.customer, () => {
      initialize()
    }, { immediate: true })

    // 年を登録するチェックボックスが変更されたら誕生日を変換
    watch(() => includeYear.value, newVal => {
      if (!formData.value.birthday) return

      const date = new Date(formData.value.birthday)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      if (newVal) {
        // 年ありに変更 - 1900年なら今年に変換
        if (date.getFullYear() === 1900) {
          const currentYear = new Date().getFullYear()
          formData.value.birthday = `${currentYear}-${month}-${day}`
        }
      } else {
        // 年なしに変更 - 1900年に変換
        formData.value.birthday = `1900-${month}-${day}`
      }
    })

    onMounted(() => {
      initialize()
    })

    return {
      form,
      formValid,
      isSubmitting,
      includeYear,
      formData,
      isPersisted,
      isNew,
      formTitle,
      formSubmitText,
      birthdayDisplay,
      birthdayPickerValue,
      onBirthdaySelect,
      clearBirthday,
      onSubmit,
      onCancel,
    }
  },
}
</script>
